import type {LabelGroup, Status} from "../types";
import {CollapsingSearch} from "./CollapsingSearch";
import {StatusFilter} from "./StatusFilter";
import {ShowFilter} from "./ShowFilter";
import styles from "./checklist.module.css";

type ChecklistMobileControlsProps = {
    hasCollection: boolean;
    searchTerm: string;
    filterState: {
        statusFilterSelected: Record<string, (Status | string)[]>;
        showGroups: LabelGroup[];
        resetKey: string | number;
    };
    onSearchChange: (term: string) => void;
    onStatusToggle: (id: string, status: Status | string) => void;
};

/**
 * Narrow-viewport chip row for the checklist view — a horizontally-scrollable
 * row of the client-side filter comboboxes plus a collapsing search. The
 * "Open all" control is intentionally dropped on narrow viewports.
 *
 * A deliberately separate component from the desktop `ChecklistControls` (not a
 * width-variant fork) so the two layouts stay isolated; only the logic-bearing
 * `StatusFilter` / `ShowFilter` / `CollapsingSearch` are shared.
 */
export const ChecklistMobileControls = ({
    hasCollection,
    searchTerm,
    filterState: {statusFilterSelected, showGroups, resetKey},
    onSearchChange,
    onStatusToggle,
}: ChecklistMobileControlsProps) => {
    return (
        <div className={styles.mobileControls}>
            <div className={styles.chipRow}>
                {hasCollection && (
                    <StatusFilter
                        key={`status-${resetKey}`}
                        selected={statusFilterSelected["status"] ?? []}
                        onToggle={(status) => onStatusToggle("status", status)}
                    />
                )}

                {showGroups.map((group) => (
                    <ShowFilter
                        key={`${group.id}-${resetKey}`}
                        group={group}
                        selected={
                            (statusFilterSelected[group.id] as string[]) ?? []
                        }
                        onToggle={(labelId) => onStatusToggle(group.id, labelId)}
                    />
                ))}
            </div>

            <CollapsingSearch
                key={`search-${resetKey}`}
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
            />
        </div>
    );
};
